import { useCallback, useEffect, useRef, useState } from 'react';
import adminApiClient from '@/services/adminApiClient';
import type {
  Coupon,
  PaymentHistoryItem,
  PlanLimit,
  RevenueData,
  SubscriptionRecord,
} from '@/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLANS = ['free', 'student', 'pro', 'premium'];
const BILLING_CYCLES = ['monthly', 'annual'];

const PLAN_COLORS: Record<string, string> = {
  free:    'bg-slate-100 text-slate-600',
  student: 'bg-blue-100 text-blue-700',
  pro:     'bg-violet-100 text-violet-700',
  premium: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS: Record<string, string> = {
  active:    'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
  expired:   'bg-orange-100 text-orange-600',
  past_due:  'bg-yellow-100 text-yellow-700',
};

const EVENT_COLORS: Record<string, string> = {
  payment_completed:   'bg-green-100 text-green-700',
  subscription_renewed:'bg-blue-100 text-blue-700',
  admin_plan_override: 'bg-violet-100 text-violet-700',
  refund_processed:    'bg-orange-100 text-orange-600',
  free_activated:      'bg-slate-100 text-slate-600',
};

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtCurrency(amount: number | null, code: string | null) {
  if (amount == null || !code) return '—';
  return new Intl.NumberFormat('en', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(amount / 100);
}

function PlanBadge({ plan }: { plan: string | null }) {
  if (!plan) return <span className="text-slate-400">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[plan] ?? 'bg-slate-100 text-slate-600'}`}>
      {plan}
    </span>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-slate-400">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] ?? 'bg-slate-100 text-slate-600'}`}>
      {status}
    </span>
  );
}

function EventBadge({ type }: { type: string }) {
  const label = type.replace(/_/g, ' ');
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${EVENT_COLORS[type] ?? 'bg-slate-100 text-slate-600'}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <div className="bg-white border border-admin-border rounded-xl p-5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`text-3xl font-semibold mt-1 ${accent ?? 'text-slate-800'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Subscriptions List
// ---------------------------------------------------------------------------

function SubscriptionsTab() {
  const [items, setItems] = useState<SubscriptionRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Plan override modal
  const [overrideTarget, setOverrideTarget] = useState<SubscriptionRecord | null>(null);
  const [overridePlan, setOverridePlan] = useState('');
  const [overrideCycle, setOverrideCycle] = useState('monthly');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminApiClient.get('/subscriptions', {
      params: { page, page_size: PAGE_SIZE, search, plan: planFilter, status: statusFilter },
    })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, search, planFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  function openOverride(sub: SubscriptionRecord) {
    setOverrideTarget(sub);
    setOverridePlan(sub.plan_id);
    setOverrideCycle(sub.billing_cycle ?? 'monthly');
    setOverrideReason('');
    setOverrideError('');
  }

  async function submitOverride() {
    if (!overrideTarget) return;
    setOverrideLoading(true);
    setOverrideError('');
    try {
      await adminApiClient.patch(`/subscriptions/${overrideTarget.user_id}/plan`, {
        plan_id: overridePlan,
        billing_cycle: overridePlan === 'free' ? undefined : overrideCycle,
        reason: overrideReason || undefined,
      });
      setOverrideTarget(null);
      load();
    } catch (e: any) {
      setOverrideError(e.response?.data?.detail ?? 'Failed to update plan');
    } finally {
      setOverrideLoading(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search by email or name…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
          value={planFilter}
          onChange={e => { setPlanFilter(e.target.value); setPage(1); }}
        >
          <option value="">All plans</option>
          {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          {['active', 'cancelled', 'expired', 'past_due'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="ml-auto text-sm text-slate-500 self-center">{total.toLocaleString()} total</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Billing</th>
              <th className="text-left px-4 py-3">Currency</th>
              <th className="text-left px-4 py-3">Period End</th>
              <th className="text-left px-4 py-3">Gateway</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-slate-400 py-10">No subscriptions found</td></tr>
            ) : items.map(sub => (
              <tr key={sub.user_id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{sub.user_name ?? sub.user_email}</div>
                  {sub.user_name && <div className="text-xs text-slate-400">{sub.user_email}</div>}
                </td>
                <td className="px-4 py-3"><PlanBadge plan={sub.plan_id} /></td>
                <td className="px-4 py-3"><StatusBadge status={sub.status} /></td>
                <td className="px-4 py-3 text-slate-600">{sub.billing_cycle ?? '—'}</td>
                <td className="px-4 py-3 text-slate-600">{sub.currency_code ?? '—'}</td>
                <td className="px-4 py-3 text-slate-600">{fmt(sub.current_period_end)}</td>
                <td className="px-4 py-3 text-slate-600">{sub.gateway ?? '—'}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => openOverride(sub)}
                    className="text-indigo-600 hover:underline text-xs font-medium"
                  >
                    Change Plan
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50"
          >
            ← Prev
          </button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50"
          >
            Next →
          </button>
        </div>
      )}

      {/* Override Modal */}
      {overrideTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Change Plan</h2>
            <p className="text-sm text-slate-500 mb-4">
              User: <span className="font-medium text-slate-700">{overrideTarget.user_email}</span>
            </p>

            <label className="block text-sm font-medium text-slate-700 mb-1">New Plan</label>
            <select
              className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none"
              value={overridePlan}
              onChange={e => setOverridePlan(e.target.value)}
            >
              {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            {overridePlan !== 'free' && (
              <>
                <label className="block text-sm font-medium text-slate-700 mb-1">Billing Cycle</label>
                <select
                  className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none"
                  value={overrideCycle}
                  onChange={e => setOverrideCycle(e.target.value)}
                >
                  {BILLING_CYCLES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </>
            )}

            <label className="block text-sm font-medium text-slate-700 mb-1">Reason (optional)</label>
            <input
              className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none"
              placeholder="e.g. Promotional override"
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
            />

            {overrideError && <p className="text-red-500 text-sm mb-3">{overrideError}</p>}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setOverrideTarget(null)}
                className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={submitOverride}
                disabled={overrideLoading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {overrideLoading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Payments & Refunds
// ---------------------------------------------------------------------------

function PaymentsTab() {
  const [items, setItems] = useState<PaymentHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const [search, setSearch] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Refund modal
  const [refundTarget, setRefundTarget] = useState<PaymentHistoryItem | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundError, setRefundError] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    adminApiClient.get('/subscriptions/payments', {
      params: { page, page_size: PAGE_SIZE, search, event_type: eventTypeFilter },
    })
      .then(r => { setItems(r.data.items); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [page, search, eventTypeFilter]);

  useEffect(() => { load(); }, [load]);

  async function submitRefund() {
    if (!refundTarget || !refundReason.trim()) return;
    setRefundLoading(true);
    setRefundError('');
    try {
      await adminApiClient.post(`/subscriptions/payments/${refundTarget.id}/refund`, {
        reason: refundReason,
      });
      setRefundTarget(null);
      load();
    } catch (e: any) {
      setRefundError(e.response?.data?.detail ?? 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          className="border border-admin-border rounded-lg px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          placeholder="Search by email or name…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select
          className="border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
          value={eventTypeFilter}
          onChange={e => { setEventTypeFilter(e.target.value); setPage(1); }}
        >
          <option value="">All event types</option>
          {['payment_completed', 'subscription_renewed', 'admin_plan_override', 'refund_processed', 'free_activated'].map(t => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-slate-500 self-center">{total.toLocaleString()} events</span>
      </div>

      <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
              <th className="text-left px-4 py-3">User</th>
              <th className="text-left px-4 py-3">Event</th>
              <th className="text-left px-4 py-3">Plan</th>
              <th className="text-left px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Refund</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 py-10">No payment events found</td></tr>
            ) : items.map(evt => (
              <tr key={evt.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{evt.user_name ?? evt.user_email}</div>
                  {evt.user_name && <div className="text-xs text-slate-400">{evt.user_email}</div>}
                </td>
                <td className="px-4 py-3"><EventBadge type={evt.event_type} /></td>
                <td className="px-4 py-3"><PlanBadge plan={evt.plan_id} /></td>
                <td className="px-4 py-3 text-slate-700">{fmtCurrency(evt.amount, evt.currency_code)}</td>
                <td className="px-4 py-3">
                  {evt.refund_status === 'refunded' ? (
                    <span className="text-orange-600 text-xs font-medium">Refunded {fmt(evt.refunded_at)}</span>
                  ) : (
                    <span className="text-slate-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-500">{fmt(evt.created_at)}</td>
                <td className="px-4 py-3">
                  {['payment_completed', 'subscription_renewed'].includes(evt.event_type) &&
                    evt.refund_status !== 'refunded' && (
                    <button
                      onClick={() => { setRefundTarget(evt); setRefundReason(''); setRefundError(''); }}
                      className="text-red-500 hover:underline text-xs font-medium"
                    >
                      Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50"
          >
            ← Prev
          </button>
          <span className="text-slate-500">Page {page} of {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 border border-admin-border rounded-lg disabled:opacity-40 hover:bg-slate-50"
          >
            Next →
          </button>
        </div>
      )}

      {/* Refund Modal */}
      {refundTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Process Refund</h2>
            <p className="text-sm text-slate-500 mb-1">
              User: <span className="font-medium text-slate-700">{refundTarget.user_email}</span>
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Amount: <span className="font-medium text-slate-700">{fmtCurrency(refundTarget.amount, refundTarget.currency_code)}</span>
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              This will mark the payment as refunded in the database and downgrade the user to the free plan. No gateway API call is made.
            </p>

            <label className="block text-sm font-medium text-slate-700 mb-1">Reason <span className="text-red-500">*</span></label>
            <input
              className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none"
              placeholder="e.g. User requested cancellation"
              value={refundReason}
              onChange={e => setRefundReason(e.target.value)}
            />

            {refundError && <p className="text-red-500 text-sm mb-3">{refundError}</p>}

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRefundTarget(null)}
                className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={submitRefund}
                disabled={refundLoading || !refundReason.trim()}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {refundLoading ? 'Processing…' : 'Process Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Revenue Dashboard
// ---------------------------------------------------------------------------

function RevenueTab() {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApiClient.get('/subscriptions/revenue')
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-slate-400 text-sm py-10 text-center">Loading revenue data…</p>;
  if (!data) return <p className="text-red-500 text-sm py-10 text-center">Failed to load</p>;

  const currencies = Object.keys(data.revenue_by_currency);
  const totalDistribution = Object.values(data.plan_distribution).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Paying Users" value={data.paying_users_total} accent="text-indigo-700" />
        <StatCard label="New Paying (This Month)" value={data.new_paying_this_month} accent="text-green-700" />
        <StatCard label="Cancelled (This Month)" value={data.cancelled_this_month} accent="text-red-600" />
        <StatCard label="Active Currencies" value={currencies.length || 0} />
      </div>

      {/* MRR Per Currency */}
      {currencies.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">MRR by Currency</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {currencies.map(currency => {
              const entry = data.revenue_by_currency[currency];
              return (
                <div key={currency} className="bg-white border border-admin-border rounded-xl p-5">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-slate-500">{currency} MRR</p>
                      <p className="text-2xl font-semibold text-slate-800 mt-1">
                        {fmtCurrency(entry.mrr, currency)}
                      </p>
                    </div>
                    <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-1 rounded-lg font-medium">
                      {entry.paying_users} users
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Plan Distribution */}
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">Plan Distribution</h3>
        <div className="bg-white border border-admin-border rounded-xl p-5">
          <div className="space-y-3">
            {PLANS.map(plan => {
              const count = data.plan_distribution[plan] ?? 0;
              const pct = totalDistribution > 0 ? (count / totalDistribution) * 100 : 0;
              return (
                <div key={plan}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700 capitalize">{plan}</span>
                    <span className="text-slate-500">{count.toLocaleString()} ({pct.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        plan === 'free' ? 'bg-slate-400' :
                        plan === 'student' ? 'bg-blue-500' :
                        plan === 'pro' ? 'bg-violet-500' : 'bg-amber-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          {totalDistribution > 0 && (
            <p className="text-xs text-slate-400 mt-4">{totalDistribution.toLocaleString()} total subscribers</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Plan Limits
// ---------------------------------------------------------------------------

type LimitRow = PlanLimit & { _dirty: boolean; _saving: boolean; _saved: boolean };

const LIMIT_FIELDS: { key: keyof PlanLimit; label: string }[] = [
  { key: 'tutor_monthly',    label: 'Tutor / mo' },
  { key: 'exam_monthly',     label: 'Exam / mo' },
  { key: 'practice_monthly', label: 'Practice / mo' },
  { key: 'notes_monthly',    label: 'Notes / mo' },
  { key: 'tutor_daily',      label: 'Tutor / day' },
  { key: 'exam_daily',       label: 'Exam / day' },
  { key: 'practice_daily',   label: 'Practice / day' },
  { key: 'notes_daily',      label: 'Notes / day' },
];

function PlanLimitsTab() {
  const [rows, setRows] = useState<LimitRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApiClient.get('/subscriptions/plan-limits')
      .then(r => setRows(r.data.map((row: PlanLimit) => ({ ...row, _dirty: false, _saving: false, _saved: false }))))
      .finally(() => setLoading(false));
  }, []);

  function handleChange(planId: string, field: string, raw: string) {
    setRows(prev => prev.map(r =>
      r.plan_id === planId
        ? { ...r, [field]: raw === '' ? null : Number(raw), _dirty: true, _saved: false }
        : r
    ));
  }

  async function saveRow(planId: string) {
    const row = rows.find(r => r.plan_id === planId);
    if (!row) return;
    setRows(prev => prev.map(r => r.plan_id === planId ? { ...r, _saving: true } : r));
    try {
      await adminApiClient.put(`/subscriptions/plan-limits/${planId}`, {
        tutor_monthly:    row.tutor_monthly,
        exam_monthly:     row.exam_monthly,
        practice_monthly: row.practice_monthly,
        notes_monthly:    row.notes_monthly,
        tutor_daily:      row.tutor_daily,
        exam_daily:       row.exam_daily,
        practice_daily:   row.practice_daily,
        notes_daily:      row.notes_daily,
      });
      setRows(prev => prev.map(r =>
        r.plan_id === planId ? { ...r, _saving: false, _dirty: false, _saved: true } : r
      ));
      setTimeout(() => setRows(prev => prev.map(r =>
        r.plan_id === planId ? { ...r, _saved: false } : r
      )), 2000);
    } catch {
      setRows(prev => prev.map(r => r.plan_id === planId ? { ...r, _saving: false } : r));
    }
  }

  if (loading) return <p className="text-slate-400 text-sm py-10 text-center">Loading…</p>;

  return (
    <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
            <th className="text-left px-4 py-3 sticky left-0 bg-slate-50">Plan</th>
            {LIMIT_FIELDS.map(f => (
              <th key={f.key} className="text-center px-3 py-3 whitespace-nowrap">{f.label}</th>
            ))}
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.plan_id} className="border-b border-admin-border last:border-0">
              <td className="px-4 py-3 sticky left-0 bg-white">
                <PlanBadge plan={row.plan_id} />
              </td>
              {LIMIT_FIELDS.map(f => (
                <td key={f.key} className="px-3 py-2 text-center">
                  <input
                    type="number"
                    min={0}
                    className="w-20 text-center border border-admin-border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                    value={row[f.key] ?? ''}
                    placeholder="∞"
                    onChange={e => handleChange(row.plan_id, f.key, e.target.value)}
                  />
                </td>
              ))}
              <td className="px-4 py-3 text-right whitespace-nowrap">
                {row._saved ? (
                  <span className="text-green-600 text-xs font-medium">Saved ✓</span>
                ) : (
                  <button
                    onClick={() => saveRow(row.plan_id)}
                    disabled={!row._dirty || row._saving}
                    className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
                  >
                    {row._saving ? 'Saving…' : 'Save'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-400 px-4 py-3 border-t border-admin-border">
        Leave a field blank to set unlimited (∞). Changes are applied immediately to all new requests.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab: Coupons
// ---------------------------------------------------------------------------

function CouponsTab() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    code: '',
    description: '',
    discount_type: 'percent' as 'percent' | 'fixed',
    discount_value: '',
    applicable_plans: [] as string[],
    max_redemptions: '',
    expires_at: '',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const load = useCallback(() => {
    adminApiClient.get('/subscriptions/coupons')
      .then(r => setCoupons(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function togglePlan(plan: string) {
    setForm(prev => ({
      ...prev,
      applicable_plans: prev.applicable_plans.includes(plan)
        ? prev.applicable_plans.filter(p => p !== plan)
        : [...prev.applicable_plans, plan],
    }));
  }

  async function createCoupon() {
    setFormError('');
    if (!form.code.trim()) { setFormError('Coupon code is required'); return; }
    if (!form.discount_value) { setFormError('Discount value is required'); return; }
    setFormLoading(true);
    try {
      await adminApiClient.post('/subscriptions/coupons', {
        code: form.code.trim().toUpperCase(),
        description: form.description || undefined,
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value),
        applicable_plans: form.applicable_plans,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : undefined,
        expires_at: form.expires_at || undefined,
      });
      setShowForm(false);
      setForm({ code: '', description: '', discount_type: 'percent', discount_value: '', applicable_plans: [], max_redemptions: '', expires_at: '' });
      load();
    } catch (e: any) {
      setFormError(e.response?.data?.detail ?? 'Failed to create coupon');
    } finally {
      setFormLoading(false);
    }
  }

  async function toggleCoupon(code: string) {
    await adminApiClient.patch(`/subscriptions/coupons/${code}/toggle`);
    load();
  }

  async function deleteCoupon(code: string) {
    try {
      await adminApiClient.delete(`/subscriptions/coupons/${code}`);
      setDeleteTarget(null);
      load();
    } catch (e: any) {
      alert(e.response?.data?.detail ?? 'Failed to delete coupon');
    }
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          + New Coupon
        </button>
      </div>

      {loading ? (
        <p className="text-slate-400 text-sm text-center py-10">Loading…</p>
      ) : (
        <div className="bg-white border border-admin-border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-admin-border text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Discount</th>
                <th className="text-left px-4 py-3">Plans</th>
                <th className="text-left px-4 py-3">Redemptions</th>
                <th className="text-left px-4 py-3">Expires</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {coupons.length === 0 ? (
                <tr><td colSpan={8} className="text-center text-slate-400 py-10">No coupons yet</td></tr>
              ) : coupons.map(c => (
                <tr key={c.id} className="border-b border-admin-border last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-slate-800">{c.code}</span>
                    {c.description && <div className="text-xs text-slate-400">{c.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{c.discount_type}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    {c.discount_type === 'percent' ? `${c.discount_value}%` : `${c.discount_value} off`}
                  </td>
                  <td className="px-4 py-3">
                    {c.applicable_plans.length === 0 ? (
                      <span className="text-slate-400 text-xs">All plans</span>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {c.applicable_plans.map(p => <PlanBadge key={p} plan={p} />)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {c.redemption_count}
                    {c.max_redemptions != null && <span className="text-slate-400"> / {c.max_redemptions}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(c.expires_at)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {c.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={() => toggleCoupon(c.code)}
                        className="text-indigo-600 hover:underline text-xs font-medium"
                      >
                        {c.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => setDeleteTarget(c.code)}
                        className="text-red-500 hover:underline text-xs font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Coupon Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">Create Coupon</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code <span className="text-red-500">*</span></label>
                <input
                  className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm font-mono uppercase focus:outline-none"
                  placeholder="SUMMER25"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <input
                  className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                  placeholder="Summer promotion 2025"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Discount Type <span className="text-red-500">*</span></label>
                  <select
                    className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                    value={form.discount_type}
                    onChange={e => setForm(f => ({ ...f, discount_type: e.target.value as 'percent' | 'fixed' }))}
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Value <span className="text-red-500">*</span>
                    <span className="text-slate-400 font-normal ml-1">
                      {form.discount_type === 'percent' ? '(1–100)' : '(in smallest unit)'}
                    </span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={form.discount_type === 'percent' ? 100 : undefined}
                    className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                    value={form.discount_value}
                    onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Applicable Plans <span className="text-slate-400 font-normal">(leave empty = all plans)</span></label>
                <div className="flex gap-2 flex-wrap">
                  {PLANS.map(p => (
                    <button
                      key={p}
                      onClick={() => togglePlan(p)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        form.applicable_plans.includes(p)
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-admin-border hover:border-indigo-300'
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max Redemptions</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                    placeholder="Unlimited"
                    value={form.max_redemptions}
                    onChange={e => setForm(f => ({ ...f, max_redemptions: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Expires At</label>
                  <input
                    type="datetime-local"
                    className="w-full border border-admin-border rounded-lg px-3 py-2 text-sm focus:outline-none"
                    value={form.expires_at}
                    onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {formError && <p className="text-red-500 text-sm mt-3">{formError}</p>}

            <div className="flex gap-3 justify-end mt-5">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={createCoupon}
                disabled={formLoading}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {formLoading ? 'Creating…' : 'Create Coupon'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-2">Delete Coupon?</h2>
            <p className="text-sm text-slate-500 mb-5">
              Are you sure you want to delete <span className="font-mono font-semibold text-slate-800">{deleteTarget}</span>?
              This cannot be undone. Coupons that have been redeemed cannot be deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm border border-admin-border rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteCoupon(deleteTarget)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const TABS = [
  { id: 'subscriptions', label: 'Subscriptions' },
  { id: 'payments',      label: 'Payments & Refunds' },
  { id: 'revenue',       label: 'Revenue' },
  { id: 'limits',        label: 'Plan Limits' },
  { id: 'coupons',       label: 'Coupons' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function Subscriptions() {
  const [activeTab, setActiveTab] = useState<TabId>('subscriptions');

  return (
    <div>
      <h1 className="text-2xl font-semibold text-slate-800 mb-6">Subscriptions &amp; Billing</h1>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-admin-border mb-6">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'subscriptions' && <SubscriptionsTab />}
      {activeTab === 'payments'      && <PaymentsTab />}
      {activeTab === 'revenue'       && <RevenueTab />}
      {activeTab === 'limits'        && <PlanLimitsTab />}
      {activeTab === 'coupons'       && <CouponsTab />}
    </div>
  );
}
